/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import { withRouter } from 'react-router-dom';

import Loader from 'Component/Loader';
import Link from 'Component/Link';
import Overlay from 'Component/Overlay';
import CmsBlock from 'Component/CmsBlock';
import MyAccountSignIn from 'Component/MyAccountSignIn';
import MyAccountCreateAccount from 'Component/MyAccountCreateAccount';

import {
    CUSTOMER_ACCOUNT_OVERLAY_KEY
} from './MyAccountOverlay.config';
import media, { WYSIWYG_MEDIA } from "Util/Media/Media";

import { MyAccountOverlay as SourceMyAccountOverlay } from 'SourceComponent/MyAccountOverlay/MyAccountOverlay.component';
import {
    STATE_CONFIRM_EMAIL,
    STATE_CREATE_ACCOUNT,
    STATE_FORGOT_PASSWORD, STATE_FORGOT_PASSWORD_SUCCESS, STATE_LOGGED_IN,
    STATE_SIGN_IN
} from "@scandipwa/scandipwa/src/component/MyAccountOverlay/MyAccountOverlay.config";
import PropTypes from "prop-types";
import { signInStateType } from 'Type/Account';
import React, {lazy} from "react";

export const UniversalIcon = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "icons" */
    'Component/UniversalIcon'
    ));

/** @namespace Component/MyAccountOverlay/Component */
export class MyAccountOverlay extends SourceMyAccountOverlay {
    static propTypes = {
        // eslint-disable-next-line react/no-unused-prop-types
        isOverlayVisible: PropTypes.bool.isRequired,
        isLoading: PropTypes.bool.isRequired,
        state: signInStateType.isRequired,
        setSignInState: PropTypes.func.isRequired,
        setLoadingState: PropTypes.func.isRequired,
        onVisible: PropTypes.func.isRequired,
        onFormError: PropTypes.func.isRequired,
        handleForgotPassword: PropTypes.func.isRequired,
        handleSignIn: PropTypes.func.isRequired,
        handleCreateAccount: PropTypes.func.isRequired,
        isCheckout: PropTypes.bool,
        isMobile: PropTypes.bool.isRequired,
        onSignIn: PropTypes.func.isRequired
    };

    renderMap = {
        [STATE_SIGN_IN]: {
            render: () => this.renderSignIn(),
            title: __('Sign In')
        },
        [STATE_FORGOT_PASSWORD]: {
            render: () => this.renderForgotPassword(),
            title: __('Password recovery')
        },
        [STATE_FORGOT_PASSWORD_SUCCESS]: {
            render: () => this.renderForgotPasswordSuccess()
        },
        [STATE_CREATE_ACCOUNT]: {
            render: () => this.renderCreateAccount(),
            title: __('Sign Up')
        },
        [STATE_LOGGED_IN]: {
            render: () => {}
        },
        [STATE_CONFIRM_EMAIL]: {
            render: () => this.renderConfirmEmail(),
            title: __('Confirm the email')
        }
    };

    renderAccountPopupRight() {
        const { state, cmsBlock } = this.props;

        switch (state) {
            case STATE_CREATE_ACCOUNT:
                return (
                    <CmsBlock isHeader isVisible= { true } noWrapper={ true } cmsBlock= { cmsBlock['account_create_overlay'] } />
                );
            case STATE_FORGOT_PASSWORD:
                return (
                    <CmsBlock isHeader isVisible= { true } noWrapper={ true } cmsBlock= { cmsBlock['account_forgot_password_overlay'] } />
                );
            default:
                return (
                    <CmsBlock isHeader isVisible= { true } noWrapper={ true } cmsBlock= { cmsBlock['account_login_overlay'] } />
                );
        }
    }


    renderCreateAccount(isLandingPage = false) {
        const {
            state,
            handleSignIn,
            setSignInState,
            setLoadingState,
            onSignIn,
            cmsBlock
        } = this.props;

        return (
            <MyAccountCreateAccount
              cmsBlock= { cmsBlock['myAccount_migration_message'] }
              state={ state }
              handleSignIn={ handleSignIn }
              setLoadingState={ setLoadingState }
              setSignInState={ setSignInState }
              onSignIn={ onSignIn }
              isLandingPage={ isLandingPage }
            />
        );
    }

    renderSignIn() {
        const {
            state,
            onFormError,
            handleForgotPassword,
            handleCreateAccount,
            isCheckout,
            setLoadingState,
            onSignIn,
            cmsBlock
        } = this.props;

        return (
            <MyAccountSignIn
                cmsBlock= { cmsBlock['myAccount_migration_message'] }
                key={Math.random()}
                state={ state }
                onFormError={ onFormError }
                handleForgotPassword={ handleForgotPassword }
                handleCreateAccount={ handleCreateAccount }
                isCheckout={ isCheckout }
                setLoadingState={ setLoadingState }
                onSignIn={ onSignIn }
            />
        );
    }

    renderAccountClose() {
        const { hideActiveOverlay, isMobile } = this.props;

        if(isMobile){
            return (
                <Link to="/" className="Account__close">
                    <UniversalIcon
                        src={media('close.svg', WYSIWYG_MEDIA)}
                        alt="CloseIcon"
                        id="icon_close"
                        className="icon_close"
                    />
                </Link>
            );
        }

        return (

            <div onClick={hideActiveOverlay} className="Account__close">
                <UniversalIcon
                    src={media('close.svg', WYSIWYG_MEDIA)}
                    alt="CloseIcon"
                    id="icon_close"
                    className="icon_close"
                />
            </div>
        );
    }

    renderMyAccount() {
        const { state } = this.props;
        const { render, title } = this.renderMap[state];

        return (
            <div block="MyAccountOverlay" elem="Action" mods={ { state } } className="popup__forms">
                <p block="MyAccountOverlay" elem="Heading">{ title }</p>
                { render() }
            </div>
        );
    }

    render() {
        const {
            hideActiveOverlay,
            isLoading,
            onVisible,
            state,
            isCheckout,
            isMobile
        } = this.props;

        return (
            <Overlay
              id={ CUSTOMER_ACCOUNT_OVERLAY_KEY }
              mix={ { block: 'MyAccountOverlay' } }
              onVisible={ onVisible }
              isStatic={ !isCheckout && isMobile }
            >
                <a>
                    <UniversalIcon
                        src={media('close.svg', WYSIWYG_MEDIA)}
                        alt="CloseIcon"
                        id="icon_close"
                        className="popup__close"
                        style={{"height": "20px", "width": "20px"}}
                        onClick={hideActiveOverlay}
                    />
                </a>
                <div className="popup">
                    <Loader isLoading={ isLoading } />
                    <div className="popup__wrapper" >
                        { this.renderAccountClose() }
                        <div className="popup__content">
                            { this.renderMyAccount() }
                        </div>
                        { this.renderAccountPopupRight() }
                    </div>
                </div>
            </Overlay>
        );
    }
}

export default withRouter(
    MyAccountOverlay
);
